// ------------------------------------------------------------
import React from 'react';
import { Badge } from '@ui/badge';
import { cn } from '@/lib/utils';
// ------------------------------------------------------------

/**
 * UnreadWrapper component is used to wrap the children in a way that there is a small badge on the left side of the component.
 * 
 * @param children - The children of the component.
 */
function UnreadWrapper({ children, className }: UnreadWrapperProps) {
  return (
    <div className={cn('flex w-full gap-0.5', className)}>
      <ShowUnread />
      <div className="flex-grow">{children}</div>
    </div>
  );
}

type UnreadWrapperProps = {
  children: React.ReactNode;
  /**
   * The class name of the component to customize the style.
   */
  className?: string;
}

export default UnreadWrapper;

/**
 * ShowUnread component is used to show the unread badge.
 * @param className - The class name of the component to customize the style.
 */
export const ShowUnread = ({ className }: { className?: string }) => (
  <div className={cn('!bg-unread mr-0.5 w-[4px] rounded-xs', className)} />
);

/**
 * UnreadCount component shows an unread indicator as a plain orange dot (count is kept for a11y only).
 * @param count - The count of the unread, exposed via aria-label.
 * @param className - The class name of the component.
 */
export const UnreadCount = ({ count, className, hideCount = true }: UnreadCountProps) => (
  <Badge
    variant="default"
    aria-label={`${count} unread`}
    className={cn('text-foreground bg-unread rounded-full p-1 text-[10px] md:text-xs', className)}
  >
    {hideCount ? null : <span className="font-medium">{count}</span>}
  </Badge>
);

type UnreadCountProps = {
  /**
   * The count of the unread.
   */
  count: number;
  /**
   * The class name of the component to customize the style.
   */
  className?: string;
  /**
   * Whether to hide the count.
   */
  hideCount?: boolean;
}
